<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <!-- Hero Section -->
    <div class="py-12 sm:py-16">
      <div class="text-center">
        <h1
          class="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight mb-4"
        >
          Cutting Optimizer
        </h1>
        <p class="max-w-2xl mx-auto text-xl text-gray-500 mb-8">
          Minimize waste and maximize efficiency for your cutting operations
        </p>
        <div class="mt-8 flex justify-center">
          <NuxtLink
            to="/optimizer"
            class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors mr-4"
            @click="trackButtonClick('Start Optimizing 2D Cuts', 'navigation')"
          >
            Start Optimizing 2D Cuts
          </NuxtLink>
        </div>
      </div>
    </div>

    <!-- Features -->
    <div class="py-12 bg-white rounded-2xl shadow-sm border border-gray-100">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center">
          <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Choose Your Optimization Method
          </h2>
          <p class="text-lg text-gray-500 mb-10">
            Select the cutting method that best suits your needs
          </p>
        </div>

        <div
          class="flex flex-col lg:flex-row justify-center space-y-8 lg:space-y-0 lg:space-x-8"
        >
          <!-- 2D Sheet Cutting Card -->
          <div
            class="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-all flex-1 max-w-md mx-auto"
          >
            <div class="bg-gradient-to-r from-blue-500 to-blue-600 h-3"></div>
            <div class="p-6">
              <div
                class="flex items-center justify-center w-12 h-12 rounded-md bg-blue-100 text-blue-600 mb-4"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
                  />
                </svg>
              </div>
              <h3 class="text-xl font-semibold text-gray-900 mb-2">
                1D Sheet Cutting
              </h3>
              <p class="text-gray-600 mb-6">
                Optimize the cutting of rectangular parts from sheets of
                material. Perfect for plywood, metal sheets, glass, and more.
              </p>
              <NuxtLink to="/">
                <button
                  class="w-full inline-flex justify-center items-center px-4 py-2 border border-blue-500 text-blue-600 bg-white hover:bg-blue-50 rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  @click="trackButtonClick('Select 1D Optimizer', 'navigation')"
                >
                  Select 1D Optimizer
                </button>
              </NuxtLink>
            </div>
          </div>

          <!-- Advanced 2D Optimizer Card -->
          <div
            class="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-all flex-1 max-w-md mx-auto"
          >
            <div
              class="bg-gradient-to-r from-purple-500 to-purple-600 h-3"
            ></div>
            <div class="p-6">
              <div
                class="flex items-center justify-center w-12 h-12 rounded-md bg-purple-100 text-purple-600 mb-4"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
                  />
                </svg>
              </div>
              <h3 class="text-xl font-semibold text-gray-900 mb-2">
                Advanced 2D Optimizer
              </h3>
              <p class="text-gray-600 mb-6">
                For complex cutting operations with multiple constraints.
                Supports multiple strategies and detailed visualizations.
              </p>
              <NuxtLink to="/optimizer">
                <button
                  class="w-full inline-flex justify-center items-center px-4 py-2 border border-purple-500 text-purple-600 bg-white hover:bg-purple-50 rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  @click="
                    trackButtonClick('Open Advanced Optimizer', 'navigation')
                  "
                >
                  Open Advanced Optimizer
                </button>
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Selected Optimizer Section -->
    <div class="mt-12">
      <!-- Strategy Selector (only for 2D and Advanced) -->

      <!-- Optimizer Components -->
      <CuttingOptimizer v-if="activeTab === '1d'" />
      <CuttingOptimizer :selectedStrategy="selectedStrategyId" />
      <div v-if="activeTab === 'advanced'" class="text-center">
        <p class="mb-4">
          Go to our advanced optimizer for more cutting options and strategies.
        </p>
        <NuxtLink
          to="/optimizer"
          class="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-6 rounded transition-colors"
          @click="
            trackButtonClick('Open Advanced Optimizer - Bottom', 'navigation')
          "
        >
          Open Advanced Optimizer
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, watch } from 'vue';
  import CuttingOptimizer2D from '~/components/CuttingOptimizer2D.vue';
  import { StrategyFactory } from '~/composables/strategies/StrategyFactory';

  const activeTab = ref('2d'); // Default active tab
  const availableStrategies = ref([]);
  const selectedStrategyId = ref('first-fit');
  const isLoading = ref(false);

  // Initialize analytics tracking
  const { trackButtonClick } = useAnalytics();

  // Get available strategies from factory
  onMounted(() => {
    availableStrategies.value = StrategyFactory.getAvailableStrategies();

    // Load saved preference if available
    const savedStrategy = localStorage.getItem('preferredStrategy');
    if (savedStrategy) {
      selectedStrategyId.value = savedStrategy;
    }
  });

  // Save preference when changed
  watch(selectedStrategyId, (newVal) => {
    localStorage.setItem('preferredStrategy', newVal);
  });
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
